// 2026-05-26 - FormuLearning - Step 5 API 호출
// 파일 위치: frontend/src/api/step5.ts
// 2026-09-23 수정 - 이슈 #85: Mixture 합계 `mixture_total` 전달 · 분석 결과 Q² null(정의 불가) · 그래프 라벨 `labels`.

import client from './client'
import type { ApiResponse } from '../types/api'

export interface Factor {
  name: string
  min: number
  max: number
}

export interface DoeResponse {
  name: string
  target: number | null
}

export interface DoECase {
  run: number
  factors: Record<string, number>
  responses: Record<string, number | null>
}

export interface AnovaRow {
  source: string
  df: number
  ss: number
  ms: number
  f_value: number
  p_value: number
}

export interface AnalysisResult {
  anova: AnovaRow[]
  r_squared: number | null
  q_squared: number | null
}

export interface ChartLabel {
  title: string
  x: string
}

export interface GraphData {
  contour: unknown[]
  response_surface: unknown[]
  pareto: unknown[]
  main_effect: unknown[]
  interaction: unknown[]
  labels?: { main_effect?: ChartLabel; pareto?: ChartLabel }
}

export interface Step5Data {
  id: string
  project_id: string
  doe_type: 'ccd' | 'box_behnken' | 'mixture' | null
  factors: Factor[]
  responses: DoeResponse[]
  cases: DoECase[]
  analysis_result: AnalysisResult
  graph_data: GraphData
  ai_summary: string | null
  created_at: string
  updated_at: string
}

export async function getStep5(projectId: string): Promise<Step5Data> {
  const { data } = await client.get<ApiResponse<Step5Data>>(`/step5/${projectId}/`)
  return data.data!
}

export async function updateStep5(projectId: string, body: Partial<Step5Data>): Promise<Step5Data> {
  const { data } = await client.put<ApiResponse<Step5Data>>(`/step5/${projectId}/`, body)
  return data.data!
}

export async function generateCases(
  projectId: string,
  doeType: string,
  factors: Factor[],
  responses: DoeResponse[],
  mixtureTotal?: number,
): Promise<{ cases: DoECase[]; total_runs: number }> {
  const { data } = await client.post<ApiResponse<{ cases: DoECase[]; total_runs: number }>>(
    `/step5/${projectId}/generate-cases`,
    { doe_type: doeType, factors, responses, ...(mixtureTotal != null ? { mixture_total: mixtureTotal } : {}) },
  )
  return data.data!
}

export function downloadTemplateUrl(projectId: string): string {
  return `/api/v1/step5/${projectId}/download-template`
}

export async function uploadYValues(projectId: string, file: File): Promise<{ cases: DoECase[]; updated_count: number }> {
  const form = new FormData()
  form.append('file', file)
  const { data } = await client.post<ApiResponse<{ cases: DoECase[]; updated_count: number }>>(
    `/step5/${projectId}/upload-y`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return data.data!
}

export async function analyzeDoe(
  projectId: string,
  doeType: string,
  cases: DoECase[],
): Promise<{ analysis_result: AnalysisResult; graph_data: GraphData }> {
  const { data } = await client.post<ApiResponse<{ analysis_result: AnalysisResult; graph_data: GraphData }>>(
    `/step5/${projectId}/analyze`,
    { doe_type: doeType, cases },
  )
  return data.data!
}
